import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LayoutTemplate } from "lucide-react";

const field = "rounded-none border border-black bg-background h-10 px-3 focus-visible:ring-0 focus-visible:border-black";
const FIELDS = [
  ["target", "Company / Target name", "Company X"],
  ["return_pct", "Return (e.g. 30%)", "30%"],
  ["term", "Term (e.g. 65 business days)", "65 business days"],
  ["ar_base", "AR Base (e.g. $200M)", "$200M"],
  ["monthly_revenue", "Monthly Revenue (e.g. $20M)", "$20M"],
];

export const DeckEditor = ({ dealId }) => {
  const [d, setD] = useState({ target: "", return_pct: "", term: "", ar_base: "", monthly_revenue: "", summary: "", coverage_ratio: "", coverage_text: "", term_sheet: "", waterfall_intro: "", waterfall: "", faq: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/site/admin/deals/${dealId}/deck`);
      if (data.deck) setD((p) => ({ ...p, ...data.deck }));
    } catch { /* silent */ }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/site/admin/deals/${dealId}/deck`, d);
      toast.success("Deck content saved — shows on the public deck when this deal is featured");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`deck-editor-${dealId}`} className="border-t border-black p-4 space-y-4">
      <div className="flex items-center gap-2">
        <LayoutTemplate size={15} />
        <h4 className="mono text-xs uppercase tracking-[0.25em]">Public Deck Content</h4>
      </div>
      <p className="mono text-[10px] text-muted-foreground">Tailors the public deck for THIS deal — hero, summary, term sheet, downside protection, collateral coverage and FAQ. Leave any field blank to use the default. Applies whenever this deal's deck is viewed.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {FIELDS.map(([k, lbl, ph]) => (
          <div key={k}>
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">{lbl}</Label>
            <Input data-testid={`deck-${k}`} value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} className={`${field} mt-1.5`} placeholder={ph} />
          </div>
        ))}
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">Summary paragraphs (one per line)</Label>
        <textarea data-testid="deck-summary" value={d.summary || ""} onChange={(e) => setD({ ...d, summary: e.target.value })} rows={4}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 focus:outline-none focus:border-black resize-y" placeholder="Leave blank to use the default summary. Each line becomes its own paragraph." />
      </div>

      <div className="border-t border-dashed border-black pt-4">
        <h4 className="mono text-xs uppercase tracking-[0.25em] mb-1">Deeper Deck Sections (per deal)</h4>
        <p className="mono text-[10px] text-muted-foreground">Leave any block blank to use the defaults. Formats are shown in each placeholder.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Coverage Ratio</Label>
          <Input data-testid="deck-coverage_ratio" value={d.coverage_ratio || ""} onChange={(e) => setD({ ...d, coverage_ratio: e.target.value })} className={`${field} mt-1.5`} placeholder="100×" />
        </div>
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">Collateral Coverage text</Label>
        <textarea data-testid="deck-coverage_text" value={d.coverage_text || ""} onChange={(e) => setD({ ...d, coverage_text: e.target.value })} rows={2}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 focus:outline-none focus:border-black resize-y" placeholder="e.g. The ~$200M accounts receivable base stands behind this raise — roughly 100× coverage." />
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">Term Sheet — one row per line: Label | Value</Label>
        <textarea data-testid="deck-term_sheet" value={d.term_sheet || ""} onChange={(e) => setD({ ...d, term_sheet: e.target.value })} rows={5}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 font-mono focus:outline-none focus:border-black resize-y" placeholder={"Instrument | Short-term AR-backed note\nFixed Return | 30% on committed capital\nTerm | ~65 business days"} />
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">Downside Protection — intro line</Label>
        <textarea data-testid="deck-waterfall_intro" value={d.waterfall_intro || ""} onChange={(e) => setD({ ...d, waterfall_intro: e.target.value })} rows={2}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 focus:outline-none focus:border-black resize-y" placeholder="Leave blank to use the default intro." />
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">Downside Protection — one step per line: Heading | Detail</Label>
        <textarea data-testid="deck-waterfall" value={d.waterfall || ""} onChange={(e) => setD({ ...d, waterfall: e.target.value })} rows={5}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 font-mono focus:outline-none focus:border-black resize-y" placeholder={"Ongoing Collections Absorb Delays | The operator keeps collecting...\nRecourse to the Operator | The facility carries recourse..."} />
      </div>
      <div>
        <Label className="mono text-[10px] uppercase tracking-[0.2em]">FAQ — one per line: Question | Answer</Label>
        <textarea data-testid="deck-faq" value={d.faq || ""} onChange={(e) => setD({ ...d, faq: e.target.value })} rows={5}
          className="w-full mt-1.5 rounded-none border border-black bg-background text-sm p-3 font-mono focus:outline-none focus:border-black resize-y" placeholder={"Is there a minimum commitment? | There is no set minimum...\nHow am I repaid? | At maturity you receive principal plus the fixed return."} />
      </div>
      <button data-testid="deck-save" onClick={save} disabled={saving} className="rounded-none border border-black h-10 px-6 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save Deck Content"}</button>
    </div>
  );
};
